/*
 * This file includes helper functions that extracts the Allesseh JSON query
 * from the content sources array.
 */

import { cloneDeep } from 'lodash';
import isEqual from 'lodash.isequal';

import { getQueryRuleByArrayName } from 'components/query-rules-form/queryRulesFormUtils';
import {
  AllessehContentQueryBody,
  AllessehQueryRule,
  AllessehQueryRules,
  AllessehQueryRuleTerm,
  AllessehQueryRuleTerms
} from 'hooks/useAllessehContentQuery';
import { getSentence } from './text';

export const QUERY_CONTENT_ID_KEY = 'UpstreamOriginId';

// checks if contentId exists in Allesseh JSON query, if Allesseh JSON query exists
export const isContentIdExcludedInQueryRules = (contentId: string, allessehJsonQuery?: string | null) => {
  if (!allessehJsonQuery) {
    return false;
  }
  const parsedJsonQuery = JSON.parse(allessehJsonQuery) as AllessehContentQueryBody;
  if (!parsedJsonQuery.query?.not || parsedJsonQuery.query.not.length === 0) {
    return false;
  }
  return (
    parsedJsonQuery.query.not.findIndex(
      (rule) => 'term' in rule && rule.term.key === QUERY_CONTENT_ID_KEY && rule.term.value === contentId
    ) >= 0
  );
};

export const isNonEmptyQueryForAllessehQueryRules = (queryBody?: AllessehContentQueryBody | null) =>
  (!!queryBody?.sort && queryBody.sort.length > 0) ||
  (!!queryBody?.query?.and && queryBody.query.and.length > 0) ||
  (!!queryBody?.query?.or && queryBody.query.or.length > 0) ||
  (!!queryBody?.query?.not && queryBody.query.not.length > 0);

export const isNonEmptyAllessehJsonQuery = (allessehJsonQuery?: string | null) => {
  if (!allessehJsonQuery) {
    return false;
  }

  return isNonEmptyQueryForAllessehQueryRules(JSON.parse(allessehJsonQuery) as AllessehContentQueryBody);
};

const trimQueryValues = (query: AllessehContentQueryBody) => {
  const trimValues = (item: AllessehQueryRule) => {
    const termsItem = item as AllessehQueryRuleTerms;
    const termItem = item as AllessehQueryRuleTerm;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (termsItem.terms) {
      termsItem.terms.value = termsItem.terms.value.map((val: string) => val.trim());
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (termItem.term) {
      termItem.term.value = termItem.term.value.trim();
    }
  };
  query.query?.and?.forEach(trimValues);
  query.query?.or?.forEach(trimValues);
  query.query?.not?.forEach(trimValues);
};

const dedupeRules = (rules?: AllessehQueryRule[]): AllessehQueryRule[] =>
  rules?.reduce<AllessehQueryRule[]>((acc, rule) => (acc.some((r) => isEqual(r, rule)) ? acc : [...acc, rule]), []) ??
  [];

export const dedupeQuery = (query?: AllessehQueryRules): AllessehQueryRules => {
  const keysToDedupe = ['and', 'or', 'not'] as const;
  const safeQuery = query ?? {};

  return Object.fromEntries(keysToDedupe.map((key) => [key, dedupeRules(safeQuery[key])]));
};

export const mergeAllessehQueryBodies = (...newQueryBodyStrs: (string | undefined | null)[]) => {
  const finalQueryBody: AllessehContentQueryBody = {
    query: { and: [], or: [], not: [] },
    sort: []
  };

  newQueryBodyStrs.forEach((newQueryBodyStr) => {
    if (!newQueryBodyStr) {
      return;
    }

    const newQueryBody = JSON.parse(newQueryBodyStr) as AllessehContentQueryBody;

    if (newQueryBody.count != null) {
      finalQueryBody.count = newQueryBody.count;
    }

    if (newQueryBody.query && finalQueryBody.query) {
      finalQueryBody.query.and = finalQueryBody.query.and?.concat(newQueryBody.query.and ?? []);
      finalQueryBody.query.not = finalQueryBody.query.not?.concat(newQueryBody.query.not ?? []);
      finalQueryBody.query.or = finalQueryBody.query.or?.concat(newQueryBody.query.or ?? []);
    }

    trimQueryValues(finalQueryBody);

    // delete dedupe Rules
    finalQueryBody.query = dedupeQuery(finalQueryBody.query);

    if (newQueryBody.sort && finalQueryBody.sort) {
      // finalQueryBody.sort = finalQueryBody.sort ?? [];
      newQueryBody.sort.forEach((newSortRule) => {
        const existingIndex = finalQueryBody.sort?.findIndex(
          (existingSortRule) => existingSortRule.key === newSortRule.key
        );
        if (typeof existingIndex === 'number' && existingIndex >= 0) {
          finalQueryBody.sort?.splice(existingIndex, 1);
        }
        finalQueryBody.sort?.push(newSortRule);
      });
    }

    if (newQueryBody.aggregations) {
      finalQueryBody.aggregations = finalQueryBody.aggregations ?? [];
      finalQueryBody.aggregations = finalQueryBody.aggregations.concat(newQueryBody.aggregations);
    }
  });

  return finalQueryBody;
};

export const validateValue = (
  currentProperty: string | null,
  query?: { [key: string]: AllessehQueryRule[] | undefined },
  fetchedSchema?: Record<string, unknown> | null
) => {
  const defaultMessage = 'No items matching query rules';
  if (!query) return defaultMessage;

  const queryRuleConfig = getQueryRuleByArrayName(currentProperty, undefined, fetchedSchema);

  const keysForMissingValues = Object.keys(query)
    .map((rule) =>
      (query[rule] as AllessehQueryRule[]).reduce((accum: string[], ruleItem: AllessehQueryRule) => {
        if ('terms' in ruleItem) {
          if (!ruleItem.terms.value.length)
            accum.push(
              (queryRuleConfig[ruleItem.terms.key] as typeof queryRuleConfig[string] | undefined)?.label ??
                getSentence(ruleItem.terms.key)
            );
        }
        if ('term' in ruleItem) {
          if (!ruleItem.term.value || ruleItem.term.value.length === 0)
            accum.push(
              (queryRuleConfig[ruleItem.term.key] as typeof queryRuleConfig[string] | undefined)?.label ??
                getSentence(ruleItem.term.key)
            );
        }
        if ('date' in ruleItem) {
          if (!ruleItem.date.value || ruleItem.date.value.length === 0)
            accum.push(
              (queryRuleConfig[ruleItem.date.key] as typeof queryRuleConfig[string] | undefined)?.label ??
                getSentence(ruleItem.date.key)
            );
        }
        return accum;
      }, [])
    )
    .flat();
  return keysForMissingValues.length
    ? `A value is missing from term${keysForMissingValues.length > 1 ? 's' : ''}: ${keysForMissingValues
        .filter((value, index, array) => array.indexOf(value) === index)
        .join(', ')}. Please check your query.`
    : defaultMessage;
};

export const getExcludedIdsQuery = (allessehQueryBody: AllessehContentQueryBody | null) => {
  const excludedAllessehContent =
    allessehQueryBody?.query?.not?.filter(
      (queryItem) => 'term' in queryItem && queryItem.term.key === 'UpstreamOriginId'
    ) ?? [];
  return { query: { or: [...excludedAllessehContent] } };
};

// NOTE: This function is a copy of the `mergeQueryClauses` method from the backend file src/entities/datamodel/content/query/query.model.ts
export const mergeQueryClauses = (queryBody: AllessehContentQueryBody): AllessehContentQueryBody => {
  // Basic check for the query structure
  if (typeof queryBody !== 'object' || !queryBody.query || typeof queryBody.query !== 'object') {
    console.warn("Input query body does not contain a valid 'query' object. Returning original body.");
    return queryBody;
  }

  const newData: AllessehContentQueryBody = cloneDeep(queryBody);
  const query = newData.query as AllessehQueryRules; // Assert type after check

  const clauseKeys = Object.keys(query) as (keyof AllessehQueryRules)[];

  clauseKeys.forEach((clauseKey) => {
    // Ensure the property belongs to the object itself and is an array
    if (Object.hasOwnProperty.call(query, clauseKey) && Array.isArray(query[clauseKey])) {
      // Explicitly type the clause list
      const originalClauseList = query[clauseKey] as AllessehQueryRule[];

      // Rebuild the clause list with merged conditions, always using 'terms'
      const newClauseList: AllessehQueryRule[] = [];

      // Use a Map to store aggregated values: Key (string) -> Set<Value (string)>
      // Using a Set automatically handles duplicate values for the same key.
      const mergedConditions = new Map<string, Set<string>>();

      // Iterate through each condition object in the current clause list
      originalClauseList.forEach((conditionObj) => {
        let conditionType: 'term' | 'terms' | null = null;
        let conditionData = null;

        // Type guard to determine if it's a 'term' or 'terms' object
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (conditionObj && typeof conditionObj === 'object') {
          if ('term' in conditionObj && typeof conditionObj.term === 'object') {
            conditionType = 'term';
            conditionData = conditionObj.term;
          } else if ('terms' in conditionObj && typeof conditionObj.terms === 'object') {
            conditionType = 'terms';
            conditionData = conditionObj.terms;
          }
        }

        // Skip if the condition object is malformed or doesn't have key/value
        if (
          !conditionType ||
          !conditionData ||
          typeof conditionData.key !== 'string' ||
          typeof conditionData.value === 'undefined'
        ) {
          console.warn(`Skipping malformed condition object in '${clauseKey}':`, JSON.stringify(conditionObj));

          newClauseList.push(conditionObj); // Push the original object to the new list

          return; // continue to next item in forEach
        }

        // Normalize the key (e.g., trim whitespace)
        const keyName = conditionData.key.trim();

        // Initialize a Set for the key if it doesn't exist yet
        if (!mergedConditions.has(keyName)) {
          mergedConditions.set(keyName, new Set<string>());
        }
        const valueSet = mergedConditions.get(keyName)!; // Use non-null assertion (!) as we just ensured it exists

        // Add value(s) to the Set for the current key
        if (conditionType === 'term') {
          // 'term' has a single string value
          const termValue = conditionData.value;
          if (typeof termValue === 'string') {
            valueSet.add(termValue);
          } else {
            console.warn(
              `Expected string value for 'term' key '${keyName}', got ${typeof termValue} in '${clauseKey}'. Skipping value:`,
              termValue
            );
          }
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        } else if (conditionType === 'terms') {
          // 'terms' has an array of string values
          const termsValue = conditionData.value;
          if (Array.isArray(termsValue)) {
            termsValue.forEach((val: unknown) => {
              // Check each item in array
              if (typeof val === 'string') {
                valueSet.add(val);
              } else {
                console.warn(
                  `Expected string value within 'terms' array for key '${keyName}', got ${typeof val} in '${clauseKey}'. Skipping value:`,
                  val
                );
              }
            });
          } else {
            console.warn(
              `Expected array value for 'terms' key '${keyName}', got ${typeof termsValue} in '${clauseKey}'. Skipping value:`,
              termsValue
            );
          }
        }
      });

      mergedConditions.forEach((value, key) => {
        // Convert Set back to an array and sort for consistent output
        const sortedValues: string[] = Array.from(value).sort();
        newClauseList.push({
          terms: {
            // Always output using the 'terms' structure
            key,
            value: sortedValues
          }
        });
      });

      // Replace the original clause list with the new merged list
      query[clauseKey] = newClauseList;
    } else if (Object.hasOwnProperty.call(query, clauseKey) && !Array.isArray(query[clauseKey])) {
      // Handle cases where a property exists in query but isn't an array (like 'someOtherProperty: true')
      console.warn(`Property '${clauseKey}' within 'query' is not an array. Skipping merge for this property.`);
    }
  });

  if (Array.isArray(newData.query!.or) && newData.query!.or.length === 0) {
    // If the 'or' clause is empty, remove it from the query object
    delete newData.query!.or;
  }

  return newData;
};
